import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { usePayFeesQuery } from 'hooks/queries/usePayFees'
import { useWalletLimits } from 'hooks/queries/useWalletLimits'
import { IWalletActionApiType } from 'pages/Wallets/hooks/useWalletActionApi'
import {
  Address,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  IntermediaryBank,
} from 'types/beneficiaries'
import { Currencies, CurrencyLimits, WalletType } from 'types/types'
import { PayFee } from 'types/wallets'
import { WalletTabProps } from '../Modal/WalletModal'
import PayAmount from './PayAmount'
import PayComplete from './PayComplete'
import PayConfirm from './PayConfirm'
import PayFormBenficiary from './PayFormBenficiary'

export type PaySteps = 'form' | 'amount' | 'confirm' | 'complete'

export type PayForm = {
  alias?: string
  walletId?: string
  password?: string
  beneficiaryEntityType?: BeneficiaryEntityType
  beneficiarySelection?: string
  countryCode?: string
  currencyCode?: string
  amount?: string | number
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  businessName?: string
  errorMessage?: string
  reference?: string
  transferDestination?: BeneficiaryTransferDestination
  address?: Address
  bankAddress?: Address
  intermediaryBank?: IntermediaryBank
  bankName?: string
  // EUR
  iban?: string
  bic?: string
  // GBP
  accountNumber?: string
  sortCode?: string
  // NGN
  ngnAccountNumber?: string
  bankCode?: string
  // USD
  usdAccountNumber?: string
  abaRoutingNumber?: string
}

export type PayActionProps = WalletTabProps &
  IWalletActionApiType<PayForm, PaySteps> & {
    withdrawalLimits?: CurrencyLimits
    payFees?: PayFee
    confirmAmount?: () => void
    isLimitsError: boolean
  }

/**
 * Pay Tab containing state, form and API data for each step
 */
export const Pay = ({
  loading,
  step,
  form,
  wallets,
  setTabVisible,
  setLoading,
  setStep,
  ...otherProps
}: PayActionProps) => {
  const [selectedWallet, setSelectedWallet] = useState<WalletType>(null)
  const [withdrawalLimits, setWithdrawalLimits] = useState<CurrencyLimits | null>()
  const formValues = form.watch()

  const { payFees, refetchPayFees, fetchingPayFees } = usePayFeesQuery(
    Number(formValues?.walletId),
    Number(formValues?.amount),
    !!formValues?.walletId
  )

  const {
    data: withdrawLimits,
    isFetching: isFetchingWithdrawLimits,
    isError: isFetchingWithdrawLimitsError,
    refetch: fetchWithdrawLimits,
  } = useWalletLimits(selectedWallet?.currency?.code as Currencies, selectedWallet?.id, false)

  const confirmAmount = async () => {
    setLoading(false)
    setStep('confirm')
  }

  const refreshTransactionFee = useCallback(async () => {
    setLoading(true)
    await refetchPayFees()
    setLoading(false)
  }, [formValues?.amount])

  useEffect(() => {
    if (formValues?.amount) {
      refreshTransactionFee()
    }
  }, [formValues?.amount])

  const orchestratedPayFees = useMemo((): PayFee => {
    return {
      ...payFees,
      fee: payFees?.fee,
      feePercentage: payFees?.feePercentage,
    }
  }, [payFees])

  const props = {
    withdrawalLimits,
    payFees: orchestratedPayFees,
    loading: loading || fetchingPayFees,
    step,
    form,
    setTabVisible,
    setLoading,
    wallets,
    setStep,
    confirmAmount,
    ...otherProps,
  }

  useEffect(() => {
    if (formValues?.walletId && wallets?.length) {
      const wallet = wallets.find(wallet => wallet?.id?.toString() === formValues?.walletId)
      setSelectedWallet(wallet)
    }
  }, [formValues?.walletId, wallets])

  useEffect(() => {
    if (selectedWallet) {
      setWithdrawalLimits(null)
      fetchWithdrawLimits()
    }
  }, [selectedWallet])

  useEffect(() => {
    const limits = withdrawLimits?.data?.withdrawalLimits?.current
    limits && setWithdrawalLimits(limits)
  }, [withdrawLimits])

  useEffect(() => {
    setTabVisible?.(false)
  }, [step])

  const isBusy = useMemo(
    () => !wallets?.length || loading || isFetchingWithdrawLimits || fetchingPayFees,
    [withdrawalLimits, wallets, loading, isFetchingWithdrawLimits, fetchingPayFees]
  )

  const renderStep = () => {
    switch (step) {
      case 'form':
        return (
          <PayFormBenficiary
            {...props}
            loading={isBusy}
            isLimitsError={isFetchingWithdrawLimitsError}
          />
        )
      case 'amount':
        return <PayAmount {...props} />
      case 'confirm':
        return <PayConfirm {...props} />
      case 'complete':
        return <PayComplete {...props} />
    }
  }

  return renderStep()
}
